/**
 * Platform shell capabilities for Curva.
 *
 * Browser: file picker + download.
 * Tauri: native open/save dialogs + filesystem read/write.
 */

export type {
  ExportBytesOptions,
  ExportBytesResult,
  OpenedFile,
  PickOpenFileOptions,
} from "./types";

export { isNativeShell, isTauri } from "./detect";

import {
  browserConfirmDiscard,
  browserExportBytes,
  browserPickOpenFile,
  browserPickOpenFiles,
  browserSetWindowTitle,
} from "./browser";
import { isNativeShell } from "./detect";
import {
  tauriClearEditorScriptsFile,
  tauriConfirmDiscard,
  tauriCurvaFileExists,
  tauriExportBytes,
  tauriLoadDesktopConfigText,
  tauriLoadEditorScriptsBytes,
  tauriOnFileDrop,
  tauriOnOpenCurvaPaths,
  tauriOnWindowCloseRequested,
  tauriPickOpenFile,
  tauriPickOpenFiles,
  tauriReadDroppedFile,
  tauriReadOpenWithFile,
  tauriRequestWindowClose,
  tauriSaveDesktopConfigText,
  tauriSaveDocumentBytes,
  tauriSaveEditorScriptsBytes,
  tauriSetWindowTitle,
  tauriTakePendingOpenPaths,
} from "./tauri";
import type { ExportBytesOptions, ExportBytesResult, OpenedFile, PickOpenFileOptions } from "./types";

/** Pick a single file. */
export async function pickOpenFile(options: PickOpenFileOptions = {}): Promise<OpenedFile | undefined> {
  if (isNativeShell()) {
    return tauriPickOpenFile(options);
  }
  return browserPickOpenFile(options);
}

/** Pick one or more files. */
export async function pickOpenFiles(options: PickOpenFileOptions = {}): Promise<OpenedFile[]> {
  if (isNativeShell()) {
    return tauriPickOpenFiles(options);
  }
  return browserPickOpenFiles(options);
}

/**
 * Deliver bytes to the user (exports, downloads).
 * Native: save dialog (or `options.path` when set).
 * Browser: download.
 */
export async function exportBytes(
  bytes: Uint8Array,
  options: ExportBytesOptions,
): Promise<ExportBytesResult> {
  if (isNativeShell()) {
    return tauriExportBytes(bytes, options);
  }
  return browserExportBytes(bytes, options);
}

/**
 * Save a document package.
 * Native: write `options.path` or show Save dialog.
 * Browser: download (same as export).
 */
export async function saveDocumentBytes(
  bytes: Uint8Array,
  options: ExportBytesOptions,
): Promise<ExportBytesResult> {
  if (isNativeShell()) {
    return tauriSaveDocumentBytes(bytes, options);
  }
  return browserExportBytes(bytes, options);
}

export function setWindowTitle(title: string): void {
  if (isNativeShell()) {
    void tauriSetWindowTitle(title);
    return;
  }
  browserSetWindowTitle(title);
}

export async function confirmDiscard(message: string): Promise<boolean> {
  if (isNativeShell()) {
    return tauriConfirmDiscard(message);
  }
  return browserConfirmDiscard(message);
}

/** Desktop: intercept window close / Quit. No-op in browser. */
export async function onWindowCloseRequested(
  shouldClose: () => boolean | Promise<boolean>,
): Promise<() => void> {
  if (!isNativeShell()) {
    return () => undefined;
  }
  return tauriOnWindowCloseRequested(shouldClose);
}

/** Desktop: OS file paths dropped onto the window. */
export async function onNativeFileDrop(
  onPaths: (paths: string[]) => void | Promise<void>,
): Promise<() => void> {
  if (!isNativeShell()) {
    return () => undefined;
  }
  return tauriOnFileDrop(onPaths);
}

export async function readDroppedFile(path: string): Promise<OpenedFile> {
  if (!isNativeShell()) {
    throw new Error("readDroppedFile is only available in the native shell.");
  }
  return tauriReadDroppedFile(path);
}

/**
 * Read a `.curva` opened via Finder/Explorer (Open With / double-click).
 * Prefers the fs plugin after Rust expands its scope; falls back to a Rust read command.
 */
export async function readOpenWithFile(path: string): Promise<OpenedFile> {
  if (!isNativeShell()) {
    throw new Error("readOpenWithFile is only available in the native shell.");
  }
  try {
    return await tauriReadDroppedFile(path);
  } catch {
    return tauriReadOpenWithFile(path);
  }
}

/** Paths queued before the WebView listened (cold start Open With). */
export async function takePendingOpenPaths(): Promise<string[]> {
  if (!isNativeShell()) {
    return [];
  }
  return tauriTakePendingOpenPaths();
}

/** Live Open With / argv paths after the app is running. */
export async function onOpenCurvaPaths(
  onPaths: (paths: string[]) => void | Promise<void>,
): Promise<() => void> {
  if (!isNativeShell()) {
    return () => undefined;
  }
  return tauriOnOpenCurvaPaths(onPaths);
}

/** Request closing the main window (triggers dirty-tab confirm via onCloseRequested). */
export async function requestWindowClose(): Promise<void> {
  if (!isNativeShell()) {
    return;
  }
  await tauriRequestWindowClose();
}

/** Read desktop `config.ini` text, or undefined if missing / not native. */
export async function loadDesktopConfigText(): Promise<string | undefined> {
  if (!isNativeShell()) {
    return undefined;
  }
  return tauriLoadDesktopConfigText();
}

/** Persist desktop `config.ini` text (no-op on web). */
export async function saveDesktopConfigText(text: string): Promise<void> {
  if (!isNativeShell()) {
    return;
  }
  await tauriSaveDesktopConfigText(text);
}

/** Whether a `.curva` path exists on disk (native Open With ACL bypass). */
export async function curvaFileExists(path: string): Promise<boolean> {
  if (!isNativeShell()) {
    return false;
  }
  return tauriCurvaFileExists(path);
}

export async function loadEditorScriptsBytes(): Promise<Uint8Array | undefined> {
  if (!isNativeShell()) {
    return undefined;
  }
  return tauriLoadEditorScriptsBytes();
}

export async function saveEditorScriptsBytes(bytes: Uint8Array): Promise<void> {
  if (!isNativeShell()) {
    return;
  }
  await tauriSaveEditorScriptsBytes(bytes);
}

export async function clearEditorScriptsFile(): Promise<void> {
  if (!isNativeShell()) {
    return;
  }
  await tauriClearEditorScriptsFile();
}
